import { useEffect, useState } from 'react';
import { engineFetchJson } from '@/lib/engineHttp';

interface CodexStats {
  total_chunks: number;
  by_tier: Record<string, number>;
  by_topic: Record<string, number>;
  by_source_type: Record<string, number>;
  by_confidence: Record<string, number>;
  voice_ready_count: number;
  avg_blessing_score: number;
  growth_last_30_days: number;
}

export function MemoryTab() {
  const [stats, setStats] = useState<CodexStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(null);

    engineFetchJson<CodexStats>('/codex/browse/stats')
      .then((data) => {
        if (active) {
          setStats(data);
          setLoading(false);
        }
      })
      .catch((err) => {
        if (active) {
          setError(err instanceof Error ? err.message : 'Failed to fetch Codex stats');
          setLoading(false);
        }
      });

    return () => {
      active = false;
    };
  }, []);

  if (loading) {
    return (
      <div className="p-6 font-mono text-xs text-gray-500 animate-pulse">
        &gt; CONNECTING TO CODEX TELEMETRY CHANNEL...
      </div>
    );
  }

  if (error || !stats) {
    return (
      <div className="p-6 font-mono text-xs text-red-400 space-y-2">
        <div className="text-xs uppercase tracking-wider text-gray-600 mb-2">Memory Synthesis telemetry failure</div>
        <p>&gt; ERROR: Could not resolve Codex browse metrics from engine.</p>
        {error && <p className="text-gray-500">&gt; {error}</p>}
      </div>
    );
  }

  const {
    total_chunks,
    by_tier = {},
    by_topic = {},
    by_source_type = {},
    by_confidence = {},
    voice_ready_count,
    avg_blessing_score,
    growth_last_30_days,
  } = stats;

  return (
    <div className="p-6 font-mono text-sm text-gray-200 space-y-6 overflow-auto h-full max-h-[calc(100vh-140px)]">
      {/* Top Banner and Summary Stats */}
      <div className="flex items-baseline justify-between border-b border-gray-800 pb-3">
        <div>
          <div className="text-xs uppercase tracking-wider text-gray-500">Codex Memory Synthesis</div>
          <div className="text-lg font-bold text-white mt-1">Crystallization Metrics</div>
        </div>
        <div className="text-xs text-gray-500">
          STATUS: <span className="text-[#39FF14]">SYNCHRONIZED</span>
        </div>
      </div>

      {/* Grid of Key Performance Indicators */}
      <div className="grid grid-cols-4 gap-3">
        <div className="bg-gray-900/40 border border-gray-800 rounded p-4">
          <div className="text-[10px] uppercase tracking-wider text-gray-500">Total Chunks</div>
          <div className="text-2xl font-bold text-gray-100 mt-1">{total_chunks.toLocaleString()}</div>
          <div className="text-xs text-gray-500 mt-1">semantic nodes in database</div>
        </div>
        <div className="bg-gray-900/40 border border-gray-800 rounded p-4">
          <div className="text-[10px] uppercase tracking-wider text-gray-500">30d Growth</div>
          <div className="text-2xl font-bold text-[#39FF14] mt-1">+{growth_last_30_days.toLocaleString()}</div>
          <div className="text-xs text-gray-500 mt-1">new crystallization nodes</div>
        </div>
        <div className="bg-gray-900/40 border border-gray-800 rounded p-4">
          <div className="text-[10px] uppercase tracking-wider text-gray-500">Voice DNA Ready</div>
          <div className="text-2xl font-bold text-blue-400 mt-1">{voice_ready_count.toLocaleString()}</div>
          <div className="text-xs text-gray-500 mt-1">synthesized for audio paths</div>
        </div>
        <div className="bg-gray-900/40 border border-gray-800 rounded p-4">
          <div className="text-[10px] uppercase tracking-wider text-gray-500">Avg Blessing Score</div>
          <div className="text-2xl font-bold text-yellow-400 mt-1">{(avg_blessing_score * 100).toFixed(1)}%</div>
          <div className="text-xs text-gray-500 mt-1">director acceptance rating</div>
        </div>
      </div>

      {/* Core Split Sections */}
      <div className="grid grid-cols-2 gap-4">
        {/* Trust Tier Distribution */}
        <div className="bg-gray-900/20 border border-gray-800 rounded p-4">
          <div className="text-[10px] uppercase tracking-wider text-gray-500 mb-3">Trust Tier Allocation</div>
          {Object.keys(by_tier).length === 0 ? (
            <div className="text-xs text-gray-500 py-4 text-center">No trust tier distribution recorded.</div>
          ) : (
            <div className="space-y-2">
              {Object.entries(by_tier).map(([tier, count]) => {
                const pct = total_chunks > 0 ? (count / total_chunks) * 100 : 0;
                let barColor = 'bg-gray-700';
                let textColor = 'text-gray-400';
                if (tier.toUpperCase() === 'SOVEREIGN') {
                  barColor = 'bg-[#39FF14]/70';
                  textColor = 'text-[#39FF14]';
                } else if (tier.toUpperCase() === 'PROJECT_SCOPED' || tier.toUpperCase() === 'PROJECT') {
                  barColor = 'bg-yellow-500/70';
                  textColor = 'text-yellow-400';
                } else if (tier.toUpperCase() === 'EXTERNAL' || tier.toUpperCase() === 'RESEARCH') {
                  barColor = 'bg-blue-500/70';
                  textColor = 'text-blue-400';
                }
                return (
                  <div key={tier} className="text-xs space-y-1">
                    <div className="flex justify-between items-baseline">
                      <span className={`font-bold ${textColor}`}>{tier}</span>
                      <span className="text-gray-400">{count} ({pct.toFixed(1)}%)</span>
                    </div>
                    <div className="w-full bg-gray-900 h-1.5 rounded overflow-hidden">
                      <div className={`h-full ${barColor}`} style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Confidence Tier Allocation */}
        <div className="bg-gray-900/20 border border-gray-800 rounded p-4">
          <div className="text-[10px] uppercase tracking-wider text-gray-500 mb-3">Confidence Stratification</div>
          {Object.keys(by_confidence).length === 0 ? (
            <div className="text-xs text-gray-500 py-4 text-center">No confidence metrics recorded.</div>
          ) : (
            <div className="space-y-2">
              {Object.entries(by_confidence).map(([confidence, count]) => {
                const pct = total_chunks > 0 ? (count / total_chunks) * 100 : 0;
                let barColor = 'bg-blue-600/50';
                let textColor = 'text-blue-300';
                if (confidence.toLowerCase() === 'verified') {
                  barColor = 'bg-[#39FF14]/60';
                  textColor = 'text-[#39FF14]';
                } else if (confidence.toLowerCase() === 'extracted') {
                  barColor = 'bg-yellow-500/60';
                  textColor = 'text-yellow-300';
                } else if (confidence.toLowerCase() === 'inferred') {
                  barColor = 'bg-purple-500/60';
                  textColor = 'text-purple-300';
                }
                return (
                  <div key={confidence} className="text-xs space-y-1">
                    <div className="flex justify-between items-baseline">
                      <span className={`font-bold capitalize ${textColor}`}>{confidence}</span>
                      <span className="text-gray-400">{count} ({pct.toFixed(1)}%)</span>
                    </div>
                    <div className="w-full bg-gray-900 h-1.5 rounded overflow-hidden">
                      <div className={`h-full ${barColor}`} style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Sources & Topics Grid */}
      <div className="grid grid-cols-2 gap-4">
        {/* Source breakdown */}
        <div className="bg-gray-900/20 border border-gray-800 rounded p-4">
          <div className="text-[10px] uppercase tracking-wider text-gray-500 mb-3">Crystallized Source Types</div>
          {Object.keys(by_source_type).length === 0 ? (
            <div className="text-xs text-gray-500 py-4 text-center">No sources connected.</div>
          ) : (
            <div className="space-y-3">
              {Object.entries(by_source_type)
                .sort((a, b) => b[1] - a[1])
                .map(([source, count]) => {
                  const pct = total_chunks > 0 ? (count / total_chunks) * 100 : 0;
                  return (
                    <div key={source} className="flex items-center text-xs">
                      <span className="w-20 truncate text-gray-400 uppercase tracking-wider">{source}</span>
                      <div className="flex-1 bg-gray-900 h-2 rounded mx-3 overflow-hidden">
                        <div className="h-full bg-gray-400/30" style={{ width: `${pct}%` }} />
                      </div>
                      <span className="w-16 text-right text-gray-300 font-bold">{count}</span>
                    </div>
                  );
                })}
            </div>
          )}
        </div>

        {/* Top Semantic Topics */}
        <div className="bg-gray-900/20 border border-gray-800 rounded p-4">
          <div className="text-[10px] uppercase tracking-wider text-gray-500 mb-3">Active Semantic Clusters</div>
          {Object.keys(by_topic).length === 0 ? (
            <div className="text-xs text-gray-500 py-4 text-center">No topic tags created.</div>
          ) : (
            <div className="space-y-1 max-h-[180px] overflow-auto pr-2">
              {Object.entries(by_topic)
                .sort((a, b) => b[1] - a[1])
                .slice(0, 10)
                .map(([topic, count], index) => (
                  <div key={topic} className="flex justify-between items-center text-xs py-1 border-b border-gray-800/40">
                    <span className="text-gray-400 truncate max-w-[200px] flex items-center gap-2">
                      <span className="text-gray-600 text-[10px]">#{index + 1}</span>
                      {topic}
                    </span>
                    <span className="text-gray-300 bg-gray-900/60 px-1.5 py-0.5 border border-gray-800 rounded text-[10px]">
                      {count} nodes
                    </span>
                  </div>
                ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

